const EXPECTED_ERROR_MESSAGE_PATTERNS = [
	{
		pattern: /user already has \d+ column\(s\) in this columns tab/i,
		getType: () => "columns_in_tab",
	},
	{
		pattern: /cannot have more than \d+ tabs$/i,
		getType: () => "total",
	},
	{
		pattern: /cannot have more than \d+ (\w+) tabs/i,
		getType: (match: RegExpMatchArray) => `${match[1].toLowerCase()}_tab`,
	},
] as const;

/** Error classification utilities */

/**
 * Checks if an error is an expected business error based on error codes
 * and message patterns.
 *
 * @param error - The error object to check
 * @returns true if the error matches expected business error patterns
 */
export function isExpectedBusinessError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;

	const errorCode = getErrorCode(error);
	const errorMessage = getErrorMessage(error);

	if (errorCode !== "BAD_USER_INPUT") return false;

	return EXPECTED_ERROR_MESSAGE_PATTERNS.some(({ pattern }) =>
		pattern.test(errorMessage)
	);
}

/**
 * Extracts the error code from an error object.
 *
 * @param error - The error object to extract code from
 * @returns The error code string, or undefined if not found
 */
export function getErrorCode(error: unknown): string | undefined {
	if (!error || typeof error !== "object") return undefined;
	if ("code" in error && typeof error.code === "string") {
		return error.code;
	}
	return undefined;
}

/**
 * Extracts the error message from an error object.
 *
 * @param error - The error object to extract message from
 * @returns The error message string, or empty string if not found
 */
export function getErrorMessage(error: unknown): string {
	if (!error || typeof error !== "object") return "";
	if ("message" in error && typeof error.message === "string") {
		return error.message;
	}
	return "";
}

/** browser- and polyfill-specific messages for a fetch that never reached the server */
const networkErrorMessages: readonly string[] = [
	"Failed to fetch" /** Chrome */,
	"NetworkError when attempting to fetch resource." /** Firefox */,
	"Load failed" /** Safari */,
	"Network request failed" /** node-fetch/cross-fetch */,
];

/**
 * Checks if an error is a network-level fetch failure (the request never
 * reached the server), as opposed to an error the server responded with.
 *
 * @param error - The error object to check
 * @returns true if the error is a network-level fetch failure
 */
export function isNetworkError(error: unknown): boolean {
	return networkErrorMessages.includes(getErrorMessage(error));
}

/**
 * Extracts the numeric limit from an error message.
 *
 * @param errorMessage - The error message to parse
 * @returns The limit number shown in the error notification, or undefined if not found
 */
export function getLimitFromErrorMessage(
	errorMessage: string
): number | undefined {
	const haveMoreThanMatch = errorMessage.match(/cannot have more than (\d+)/i);
	if (haveMoreThanMatch) return parseInt(haveMoreThanMatch[1], 10);

	const alreadyHasMatch = errorMessage.match(/user already has (\d+)/i);
	if (alreadyHasMatch) return parseInt(alreadyHasMatch[1], 10);

	return undefined;
}

/**
 * Extracts the tab type from an error message using pattern matching.
 *
 * @param errorMessage - The error message to parse
 * @returns The tab type identifier, or undefined if no pattern matches
 */
export function getTabTypeFromErrorMessage(
	errorMessage: string
): string | undefined {
	for (const { pattern, getType } of EXPECTED_ERROR_MESSAGE_PATTERNS) {
		const match = errorMessage.match(pattern);
		if (match) return getType(match);
	}
	return undefined;
}
